import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { FiSun, FiMoon, FiMenu, FiX } from 'react-icons/fi'
import type { Theme, Language } from '../App'
import { t } from '../utils/translations'
import './Navbar.css'

interface NavbarProps {
  theme: Theme
  toggleTheme: () => void
  language: Language
  toggleLanguage: () => void
}

const navItems = ['about', 'skills', 'experience', 'education', 'projects', 'certificates', 'contact']

export default function Navbar({ theme, toggleTheme, language, toggleLanguage }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false)
  const [activeSection, setActiveSection] = useState('about')
  const [mobileOpen, setMobileOpen] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', onScroll)
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) setActiveSection(entry.target.id)
        })
      },
      { threshold: 0.4, rootMargin: '-60px 0px -40% 0px' }
    )
    navItems.forEach(id => {
      const el = document.getElementById(id)
      if (el) observer.observe(el)
    })
    return () => observer.disconnect()
  }, [])

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
    setMobileOpen(false)
  }

  const navLabels: Record<string, string> = {
    about: 'About Me', skills: 'Skills', experience: 'Experience',
    education: 'Education', projects: 'Projects', certificates: 'Certificates', contact: 'Contact',
  }

  return (
    <motion.header
      className={`navbar ${scrolled ? 'navbar--scrolled' : ''}`}
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
    >
      <div className="navbar__inner container">
        {/* Logo */}
        <motion.div
          className="navbar__logo"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => scrollTo('about')}
        >
          <div className="logo-icon">
            <span>K</span>
          </div>
          <span className="logo-text">Kenil Balar</span>
        </motion.div>

        {/* Desktop Nav */}
        <nav className="navbar__links">
          {navItems.map(item => (
            <button
              key={item}
              className={`nav-link ${activeSection === item ? 'active' : ''}`}
              onClick={() => scrollTo(item)}
            >
              {t(`nav.${item}`, language)}
              {activeSection === item && (
                <motion.span
                  className="nav-link__indicator"
                  layoutId="activeIndicator"
                  transition={{ type: 'spring', stiffness: 500, damping: 35 }}
                />
              )}
            </button>
          ))}
        </nav>

        {/* Controls */}
        <div className="navbar__controls">
          <motion.button
            className="control-btn"
            onClick={toggleTheme}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            aria-label="Toggle theme"
          >
            <AnimatePresence mode="wait">
              <motion.span
                key={theme}
                initial={{ rotate: -90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: 90, opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                {theme === 'dark' ? <FiMoon size={18} /> : <FiSun size={18} />}
              </motion.span>
            </AnimatePresence>
          </motion.button>

          <motion.button
            className="control-btn lang-btn"
            onClick={toggleLanguage}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            aria-label="Toggle language"
          >
            <AnimatePresence mode="wait">
              <motion.span
                key={language}
                initial={{ y: -10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 10, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="lang-flag"
              >
                {language === 'en' ? '🇺🇸' : '🇩🇪'}
              </motion.span>
            </AnimatePresence>
          </motion.button>

          {/* Mobile menu toggle */}
          <motion.button
            className="mobile-menu-btn"
            onClick={() => setMobileOpen(o => !o)}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            {mobileOpen ? <FiX size={22} /> : <FiMenu size={22} />}
          </motion.button>
        </div>
      </div>

      {/* Mobile Drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            className="mobile-drawer"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            {navItems.map((item, i) => (
              <motion.button
                key={item}
                className={`mobile-nav-link ${activeSection === item ? 'active' : ''}`}
                onClick={() => scrollTo(item)}
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: i * 0.05 }}
              >
                {navLabels[item]}
              </motion.button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  )
}
